<template>
  <div class="row mb-40">
    <div class="col-xxl-10 col-lg-8 pe-xxl-8">
      <div class="row">
        <div v-if="stateStore.activePromotion" class="col-12">
          <div class="promotion-lock-card mb-25">
            <div>
              <strong>{{ stateStore.activePromotion.name }}</strong>
              <span>
                {{
                  stateStore.activePromotion.customer_name
                    ? $t("sales_page.promotion_auto_applies_for_customer", {
                        value: promotionValueLabel,
                        customer: stateStore.activePromotion.customer_name,
                      })
                    : $t("sales_page.promotion_auto_applies_matching_customers", {
                        value: promotionValueLabel,
                      })
                }}
              </span>
            </div>
            <small>{{ $t("sales_page.promotion_disable_hint") }}</small>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-25 position-relative">
            <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.order_tax") }}</label>
            <div class="input-group">
              <input
                type="number"
                class="form-control shadow-none fs-14 bg-white rounded-start text-title"
                :placeholder="$t('sales_page.zero')"
                :value="stateStore.orderTax"
                @input="(e) => stateStore.setOrderTax(Number((e.target as HTMLInputElement).value), stateStore.orderTaxType)"
              />
              <select 
                class="form-select shadow-none fs-14 bg-light text-title" 
                style="max-width: 70px;"
                :value="stateStore.orderTaxType"
                @change="(e) => stateStore.setOrderTax(stateStore.orderTax, (e.target as HTMLSelectElement).value as 'fixed' | 'percentage')"
              >
                <option value="fixed">$</option>
                <option value="percentage">%</option>
              </select>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-25 position-relative">
            <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.order_discount") }}</label>
            <div class="input-group">
              <input
                type="number"
                class="form-control shadow-none fs-14 bg-white rounded-start text-title"
                :placeholder="$t('sales_page.zero')"
                :value="stateStore.orderDiscount"
                :disabled="!!stateStore.activePromotion"
                @input="(e) => stateStore.setOrderDiscount(Number((e.target as HTMLInputElement).value), stateStore.orderDiscountType)"
              />
              <select 
                class="form-select shadow-none fs-14 bg-light text-title" 
                style="max-width: 70px;"
                :value="stateStore.orderDiscountType"
                :disabled="!!stateStore.activePromotion"
                @change="(e) => stateStore.setOrderDiscount(stateStore.orderDiscount, (e.target as HTMLSelectElement).value as 'fixed' | 'percentage')"
              >
                <option value="fixed">$</option>
                <option value="percentage">%</option>
              </select>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-25 position-relative">
            <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.shipping_cost") }}</label>
            <div class="input-group">
              <input
                type="number"
                class="form-control shadow-none fs-14 bg-white rounded-start text-title"
                :placeholder="$t('sales_page.zero')"
                :value="stateStore.shippingCost"
                @input="(e) => stateStore.setShippingCost(Number((e.target as HTMLInputElement).value))"
              />
              <span class="input-group-text bg-light border-0 text-title">$</span>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-25">
            <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.status") }}</label>
            <select
              v-model="saleStatus"
              class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55 px-3"
            >
              <option value="pending">{{ $t("sales_page.status_pending") }}</option>
              <option value="sent">{{ $t("sales_page.status_sent") }}</option>
              <option value="completed">{{ $t("sales_page.status_completed") }}</option>
            </select>
          </div>
        </div>
        <template v-if="!isEditMode">
          <div class="col-md-6">
            <div class="form-group mb-25">
              <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.payment_amount") }}</label>
              <div class="input-group">
                <input
                  v-model.number="paymentAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control shadow-none fs-14 bg-white rounded-start text-title"
                  :placeholder="$t('sales_page.zero')"
                />
                <span class="input-group-text bg-light border-0 text-title">$</span>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-25">
              <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.payment_method") }}</label>
              <select
                v-model.number="paymentMethodId"
                class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55 px-3"
              >
                <option :value="null">{{ $t("sales_page.select_payment_method") }}</option>
                <option v-for="method in paymentMethods" :key="method.id" :value="method.id">
                  {{ method.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-25">
              <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.paid_by") }}</label>
              <input
                v-model="paidBy"
                type="text"
                class="w-100 d-block shadow-none fs-14 bg-white rounded-1 text-title"
                :placeholder="$t('sales_page.paid_by_placeholder')"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="col-xxl-2 col-lg-4">
      <div class="card border-0 rounded-1 w-xxl-5 pt-12 pb-12 mb-md-25">
        <table class="table style-two">
          <tbody>
            <tr>
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-normal">
                {{ $t("sales_page.order_tax") }} :
              </th>
              <td class="fs-14 fw-semibold lh-1 text-optional text-end">
                ${{ calculatedTax.toFixed(2) }}
              </td>
            </tr>
            <tr>
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-normal">
                {{ $t("sales_page.discount") }} :
              </th>
              <td class="fs-14 fw-semibold lh-1 text-optional text-end">
                ${{ calculatedDiscount.toFixed(2) }}
              </td>
            </tr>
            <tr>
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-normal">
                {{ $t("sales_page.shipping") }} :
              </th>
              <td class="fs-14 fw-semibold lh-1 text-optional text-end">
                ${{ stateStore.shippingCost.toFixed(2) }}
              </td>
            </tr>
            <tr>
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-semibold">
                {{ $t("sales_page.grand_total") }} :
              </th>
              <td class="fs-14 fw-bold lh-1 text-purple text-end">${{ grandTotal.toFixed(2) }}</td>
            </tr>
            <tr v-if="!isEditMode">
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-normal">
                {{ $t("sales_page.paid") }} :
              </th>
              <td class="fs-14 fw-semibold lh-1 text-optional text-end">
                ${{ paymentAmount.toFixed(2) }}
              </td>
            </tr>
            <tr v-if="!isEditMode">
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-normal">
                {{ $t("sales_page.due") }} :
              </th>
              <td class="fs-14 fw-semibold lh-1 text-optional text-end">
                ${{ balanceDue.toFixed(2) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="col-12">
      <div class="form-group mb-25">
        <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.notes") }}</label>
        <textarea
          v-model="note"
          cols="30"
          rows="10"
          :placeholder="$t('sales_page.add_note')"
          class="d-block w-100 bg-white border-0 rounded-1 resize-none fs-14 text-title"
        ></textarea>
      </div>
    </div>
    <div class="d-flex justify-content-end">
      <button
        class="btn btn-primary fs-16 fw-medium text-white px-30 py-15"
        type="button"
        @click="submitSale"
        :disabled="isSubmitting"
        v-permission="isEditMode ? 'sale_update' : 'sale_create'"
      >
        {{ isSubmitting ? $t("sales_page.submitting") : isEditMode ? $t("sales_page.update_sale") : $t("sales_page.submit_sale") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import stateStore from "../../../utils/store";
import quotationService from "../../../services/quotation";
import paymentMethodService, { type PaymentMethod } from "../../../services/paymentMethod";
import Swal from "sweetalert2";
import { formatPromotionValue } from "../../../utils/promotion";

export default defineComponent({
  name: "SubmitPurchase",
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const paymentMethods = ref<PaymentMethod[]>([]);
    const paymentAmount = ref(0);
    const paymentMethodId = ref<number | null>(null);
    const paidBy = ref("");
    const saleStatus = ref("completed");
    const note = ref("");
    const saleId = Number(route.params.id);
    const isEditMode = computed(
      () => route.name === "EditSalesPage" || route.name === "EditSalesPageWithId" || !Number.isNaN(saleId)
    );

    const subTotal = computed(() => {
      return stateStore.selectedProducts.reduce((sum, p) => {
        const discountAmount = p.discount_type === 'percentage' 
          ? (p.price * p.qty * p.discount) / 100 
          : p.discount;
        return sum + (p.price * p.qty - discountAmount);
      }, 0);
    });

    const calculatedDiscount = computed(() => {
      if (stateStore.orderDiscountType === 'percentage') {
        return (subTotal.value * stateStore.orderDiscount) / 100;
      }
      return Math.min(subTotal.value, stateStore.orderDiscount);
    });

    const calculatedTax = computed(() => {
      const amountAfterDiscount = subTotal.value - calculatedDiscount.value;
      if (stateStore.orderTaxType === 'percentage') {
        return (amountAfterDiscount * stateStore.orderTax) / 100;
      }
      return stateStore.orderTax;
    });

    const grandTotal = computed(() => {
      return subTotal.value - calculatedDiscount.value + calculatedTax.value + stateStore.shippingCost;
    });

    const balanceDue = computed(() => Math.max(0, grandTotal.value - Number(paymentAmount.value || 0)));
    const promotionValueLabel = computed(() =>
      stateStore.activePromotion ? formatPromotionValue(stateStore.activePromotion) : ""
    );

    const isSubmitting = ref(false);

    const submitSale = async () => {
      if (!stateStore.referenceNo) {
        await Swal.fire({ icon: "warning", title: t("sales_page.reference_required"), text: t("sales_page.reference_required_text") });
        return;
      }
      if (!stateStore.selectedCustomerId) {
        await Swal.fire({ icon: "warning", title: t("sales_page.customer_required"), text: t("sales_page.customer_required_text") });
        return;
      }
      if (!stateStore.selectedWarehouseId) {
        await Swal.fire({ icon: "warning", title: t("sales_page.warehouse_required"), text: t("sales_page.warehouse_required_text") });
        return;
      }
      if (stateStore.selectedProducts.length === 0) {
        await Swal.fire({ icon: "warning", title: t("sales_page.no_products"), text: t("sales_page.no_products_text") });
        return;
      }
      if (!isEditMode.value && paymentAmount.value > 0 && !paymentMethodId.value) {
        await Swal.fire({ icon: "warning", title: t("sales_page.payment_method_required"), text: t("sales_page.payment_method_required_text") });
        return;
      }
      isSubmitting.value = true;
      try {
        const paymentStatus =
          paymentAmount.value <= 0
            ? "unpaid"
            : paymentAmount.value >= grandTotal.value
              ? "paid"
              : "partial";

        const payload = {
          date: new Date().toISOString().split('T')[0],
          reference_no: stateStore.referenceNo,
          customer_id: stateStore.selectedCustomerId,
          warehouse_id: stateStore.selectedWarehouseId,
          note: note.value,
          discount: calculatedDiscount.value,
          tax: calculatedTax.value,
          shipping: stateStore.shippingCost,
          total: subTotal.value,
          grand_total: grandTotal.value,
          items: stateStore.selectedProducts.map(p => ({
            product_id: p.id,
            product_code: p.code,
            product_name: p.name,
            unit_price: p.price,
            quantity: p.qty,
            discount: p.discount_type === 'percentage' ? (p.price * p.qty * p.discount) / 100 : p.discount,
            subtotal: (p.price * p.qty) - (p.discount_type === 'percentage' ? (p.price * p.qty * p.discount) / 100 : p.discount)
          })),
          status: saleStatus.value,
          payment_status: paymentStatus,
          pos: 0,
          ...(!isEditMode.value ? {
            paid: Number(paymentAmount.value || 0),
            paid_by: paidBy.value || "Cash",
            method_id: paymentMethodId.value,
            payment_note: note.value,
            due: balanceDue.value,
          } : {}),
        };

        const salesService = (await import("../../../services/sales")).default;
        if (isEditMode.value && saleId) {
          await salesService.updateSale(saleId, payload);
        } else {
          await salesService.createSale(payload);
        }
        
        // Check for quotation conversion
        const quotationId = route.query.quotation_id;
        if (quotationId) {
          try {
            const qId = Number(Array.isArray(quotationId) ? quotationId[0] : quotationId);
            if (!isNaN(qId)) {
              const quotation = await quotationService.getQuotation(qId);
              quotation.status = 'completed'; 
              await quotationService.updateQuotation(qId, quotation);
            }
          } catch (qError) {
             console.error("Failed to update quotation status:", qError);
          }
        }
        await Swal.fire({
          icon: "success",
          title: isEditMode.value ? t("sales_page.sale_updated") : t("sales_page.sale_created"),
          text: isEditMode.value ? t("sales_page.sale_updated_successfully") : t("sales_page.sale_created_successfully"),
          timer: 1500,
          showConfirmButton: false,
        });
        stateStore.clearSelectedProducts();
        stateStore.clearPayments();
        router.push("/sales-list");
      } catch (error: any) {
        console.error("Error creating sale:", error);
        let errorMessage = error.response?.data?.error || error.response?.data?.message || t("sales_page.failed_to_save_sale");
        if (error.response?.data?.errors) {
            const errors = error.response.data.errors;
            errorMessage += "\n" + Object.values(errors).flat().join("\n");
        }
        await Swal.fire({
          icon: "error",
          title: isEditMode.value ? t("sales_page.update_failed") : t("sales_page.create_failed"),
          text: errorMessage,
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(async () => {
      if (!isEditMode.value) {
        try {
          paymentMethods.value = await paymentMethodService.getPaymentMethods();
        } catch (error) {
          console.error("Failed to fetch payment methods:", error);
        }
      }
    });

    return {
      stateStore,
      calculatedDiscount,
      calculatedTax,
      grandTotal,
      balanceDue,
      isSubmitting,
      isEditMode,
      paymentMethods,
      paymentAmount,
      paymentMethodId,
      paidBy,
      saleStatus,
      note,
      promotionValueLabel,
      submitSale
    };
  }
});
</script>

<style scoped>
.promotion-lock-card {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  border-radius: 18px;
  border: 1px solid #dbeafe;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fbff 100%);
  color: #1d4ed8;
}

.promotion-lock-card strong,
.promotion-lock-card span,
.promotion-lock-card small {
  display: block;
}

.promotion-lock-card span,
.promotion-lock-card small {
  font-size: 13px;
}
</style>
